import React from 'react';

import { Add as AddIcon } from '@mui/icons-material';
import { Box, Button, CircularProgress, Typography } from '@mui/material';

import { DisclaimerInfoFragment, useDisclaimersListQuery } from 'src/api';
import { DetailPageLayout } from 'src/components/common';
import { ModalId } from 'src/constants/modals';
import { useCurrentUserEmployer } from 'src/hooks/employer';
import { useModal } from 'src/hooks/shared';

import { DisclaimerCard } from '../disclaimer';

export const EmployerDisclaimersWrapperEMPAdmin: React.FC = () => {
  const { openModal } = useModal();

  const { employerId } = useCurrentUserEmployer();

  const { data: disclaimers, loading } = useDisclaimersListQuery({
    variables: {
      filter: {
        employerId: { equals: employerId },
      },
    },
    skip: !employerId,
  });

  const disclaimersList = React.useMemo(() => {
    return disclaimers?.disclaimerList ?? [];
  }, [disclaimers?.disclaimerList]);

  const openCreateModal = React.useCallback(() => {
    openModal(ModalId.DisclaimerEdit, {
      employerId,
      disclaimersList,
    });
  }, [disclaimersList, employerId, openModal]);

  const openEditModal = React.useCallback(
    (disclaimer: DisclaimerInfoFragment) => {
      openModal(ModalId.DisclaimerEdit, {
        employerId,
        disclaimer,
        disclaimersList,
      });
    },
    [disclaimersList, employerId, openModal],
  );

  const openDeleteModal = React.useCallback(
    (disclaimer: DisclaimerInfoFragment) => {
      openModal(ModalId.DisclaimerDelete, {
        disclaimer,
      });
    },
    [openModal],
  );

  const openDetailsModal = React.useCallback(
    (disclaimer: DisclaimerInfoFragment) => {
      openModal(ModalId.DisclaimerDetails, {
        disclaimer,
      });
    },
    [openModal],
  );

  return (
    <DetailPageLayout
      titleContent="Employer Disclaimer"
      topRightContent={
        <Button variant="contained" size="small" onClick={openCreateModal} startIcon={<AddIcon />}>
          Add disclaimer
        </Button>
      }
    >
      <Typography variant="body1" color="text.secondary">
        Use the Employer Disclaimer feature to create customized disclaimers for any of your unique
        job titles. When used properly, customized disclaimers provide clarity to verifiers,
        minimizing additional requests for information on submitted verifications.
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress color="secondary" />
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap={2} mt={2}>
          {disclaimersList.map(disclaimer => {
            return (
              <DisclaimerCard
                key={disclaimer.id}
                disclaimer={disclaimer}
                onClickEdit={() => openEditModal(disclaimer)}
                onClickDelete={() => openDeleteModal(disclaimer)}
                onClickDetails={() => openDetailsModal(disclaimer)}
              />
            );
          })}
        </Box>
      )}
    </DetailPageLayout>
  );
};
